import type { SingleEntityDefinition } from '@gzl10/nexus-sdk'
import { useTextField, useImageField, useSelectField, useColorField, useNumberField, useSwitchField } from '@gzl10/nexus-sdk/fields'

/**
 * UI Style Guide — Unified brand and visual identity
 *
 * Replaces: ui_branding, ui_theme, ui_effects, ui_accessibility.
 * Stored in single_records table as JSON with key 'ui_style_guide'.
 * Public read access (needed before login). ADMIN-only writes.
 */
export const uiStyleGuideEntity: SingleEntityDefinition = {
  type: 'single',
  realtime: 'sync',
  key: 'ui_style_guide',
  label: { en: 'Style Guide', es: 'Guía de Estilos' },
  icon: 'mdi:palette-outline',
  routePrefix: '/ui-style-guide',

  defaults: {
    appName: 'Nexus',
    logo: null,
    logoDark: null,
    favicon: null,
    primaryColor: '#3B82F6',
    font: 'space-grotesk',
    typographyScale: 'default',
    borderRadius: 8,
    glassEffect: true,
    theme: 'system',
    loginLayout: 'centered'
  },

  fields: {
    appName: useTextField({
      label: { en: 'App Name', es: 'Nombre de la App' },
      hint: { en: 'Displayed in header, browser tab and emails', es: 'Se muestra en header, pestaña del navegador y emails' },
      size: 100,
      required: true
    }),

    logo: useImageField({
      label: { en: 'Logo (Light Theme)', es: 'Logo (Tema Claro)' },
      hint: { en: 'SVG or PNG with transparent background, max 200px height', es: 'SVG o PNG con fondo transparente, máx 200px de alto' },
      folder: 'branding',
      visibility: 'public',
      dedupe: true
    }),

    logoDark: useImageField({
      label: { en: 'Logo (Dark Theme)', es: 'Logo (Tema Oscuro)' },
      hint: { en: 'Lighter version for dark backgrounds', es: 'Versión más clara para fondos oscuros' },
      folder: 'branding',
      visibility: 'public',
      dedupe: true
    }),

    favicon: useImageField({
      label: { en: 'Favicon', es: 'Favicon' },
      hint: { en: '32x32 or 64x64 PNG/ICO', es: '32x32 o 64x64 PNG/ICO' },
      accept: 'image/x-icon,image/png,image/svg+xml',
      maxSize: '256KB',
      folder: 'branding',
      visibility: 'public',
      dedupe: true
    }),

    primaryColor: useColorField({
      label: { en: 'Primary Color', es: 'Color Principal' },
      hint: { en: 'Accent color for buttons, links and highlights', es: 'Color de acento para botones, enlaces y resaltados' }
    }),

    font: useSelectField({
      label: { en: 'Font', es: 'Fuente' },
      hint: { en: 'Primary font for headings and UI', es: 'Fuente principal para títulos e interfaz' },
      options: [
        { value: 'space-grotesk', label: 'Space Grotesk' },
        { value: 'inter', label: 'Inter' },
        { value: 'plus-jakarta-sans', label: 'Plus Jakarta Sans' },
        { value: 'outfit', label: 'Outfit' },
        { value: 'rubik', label: 'Rubik' },
        { value: 'system', label: { en: 'System Default', es: 'Sistema' } }
      ]
    }),

    typographyScale: useSelectField({
      label: { en: 'Typography Scale', es: 'Escala Tipográfica' },
      hint: { en: 'Font size scaling (WCAG 1.4.4)', es: 'Escala de tamaños de fuente (WCAG 1.4.4)' },
      options: [
        { value: 'compact', label: { en: 'Compact', es: 'Compacta' } },
        { value: 'default', label: { en: 'Default', es: 'Por defecto' } },
        { value: 'relaxed', label: { en: 'Relaxed', es: 'Relajada' } }
      ]
    }),

    borderRadius: useNumberField({
      label: { en: 'Border Radius', es: 'Radio de Bordes' },
      hint: { en: 'Corner roundness in pixels (0 = sharp, 16 = very round)', es: 'Redondeo de esquinas en píxeles (0 = cuadrado, 16 = muy redondo)' },
      defaultValue: 8,
      validation: { min: 0, max: 16 }
    }),

    glassEffect: useSwitchField({
      label: { en: 'Glass Effect', es: 'Efecto Glass' },
      hint: { en: 'Glassmorphism blur on cards and modals', es: 'Desenfoque glassmorphism en cards y modales' },
      defaultValue: true
    }),

    theme: useSelectField({
      label: { en: 'Theme', es: 'Tema' },
      hint: { en: 'System follows device preferences', es: 'Sistema sigue las preferencias del dispositivo' },
      options: [
        { value: 'light', label: { en: 'Light', es: 'Claro' } },
        { value: 'dark', label: { en: 'Dark', es: 'Oscuro' } },
        { value: 'system', label: { en: 'System', es: 'Sistema' } }
      ]
    }),

    loginLayout: useSelectField({
      label: { en: 'Login Layout', es: 'Diseño de Login' },
      hint: { en: 'Auth page visual layout', es: 'Diseño visual de páginas de autenticación' },
      options: [
        { value: 'centered', label: { en: 'Centered', es: 'Centrado' } },
        { value: 'split', label: { en: 'Split', es: 'Dividido' } },
        { value: 'cover', label: { en: 'Cover', es: 'Portada' } },
        { value: 'floating', label: { en: 'Floating', es: 'Flotante' } },
        { value: 'minimal', label: { en: 'Minimal', es: 'Minimalista' } }
      ]
    })
  },

  casl: {
    subject: 'UiStyleGuide',
    permissions: {
      '*': { actions: ['read'] },
      ADMIN: { actions: ['read', 'update'] }
    }
  }
}
